import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import AmbientBackdrop from "../components/AmbientBackdrop";
import { useAuth } from "../context/AuthContext";
import { Mail, Lock, User as UserIcon, Loader2 } from "lucide-react";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = mode === "register"
        ? await register(email, password, name)
        : await login(email, password);
      toast.success(mode === "register" ? "Welcome to MindMoves!" : "Welcome back!");
      if (u.role === "admin") navigate("/admin");
      else navigate(u.onboarding_complete ? "/home" : "/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mm-frame mm-glow relative flex flex-col" data-testid="auth-page">
      <AmbientBackdrop />
      <div className="relative z-10 flex-1 flex flex-col px-7 pt-16 pb-10">
        <button onClick={() => navigate("/welcome")}><Logo size={40} withText textClass="text-navy text-lg" /></button>

        <div className="mt-10">
          <h1 className="font-serif text-3xl font-bold text-navy">
            {mode === "register" ? "Create your account" : "Welcome back"}
          </h1>
          <p className="text-slate-500 mt-1.5">
            {mode === "register" ? "Start training your mental game today." : "Let's get back to it."}
          </p>
        </div>

        <button data-testid="google-auth-btn" onClick={google}
          className="mt-8 w-full flex items-center justify-center gap-3 bg-white border border-slate-300 rounded-2xl py-3.5 font-semibold text-navy active:scale-[0.98] transition-transform">
          <img src="https://www.google.com/favicon.ico" alt="" className="w-5 h-5" />
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-6">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs text-slate-400 font-medium">or with email</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <div className="relative">
              <UserIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="auth-name-input" required value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Your name" className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-slate-100 border border-transparent focus:border-royal focus:bg-white outline-none transition" />
            </div>
          )}
          <div className="relative">
            <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Email" className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-slate-100 border border-transparent focus:border-royal focus:bg-white outline-none transition" />
          </div>
          <div className="relative">
            <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="auth-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password" className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-slate-100 border border-transparent focus:border-royal focus:bg-white outline-none transition" />
          {mode === "login" && (
            <div className="text-right -mt-1"><Link to="/forgot-password" data-testid="auth-forgot-link" className="text-xs font-semibold text-royal">Forgot password?</Link></div>
          )}
          </div>
          <button data-testid="auth-submit-btn" disabled={busy} type="submit"
            className="w-full bg-royal text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60">
            {busy && <Loader2 size={18} className="animate-spin" />}
            {mode === "register" ? "Create account" : "Log in"}
          </button>
        </form>

        <p className="text-center text-slate-500 text-sm mt-6">
          {mode === "register" ? "Already have an account?" : "New to MindMoves?"}{" "}
          <button data-testid="auth-toggle-btn" onClick={() => setMode(mode === "register" ? "login" : "register")}
            className="text-royal font-bold">
            {mode === "register" ? "Log in" : "Create one"}
          </button>
        </p>
      </div>
    </div>
  );
}
